"use client";

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { COLOR_SCHEMES } from "@/lib/colorSchemes";
import type {
  BannerProps,
  BannerHeight,
  BottomCurveConfig,
  DecorationConfig,
} from "./types";
import { BANNER_HEIGHT_REM, Z } from "./types";

// -----------------------------------------------------------------------------
// BannerShell — owns container, color scheme, decoration, bottom curve.
// Variants render their content as `children` inside this shell.
//
// NOTE: Do NOT lazy-load this component or its children. All banner files
// are static imports resolved at build time into a single chunk. Using
// React.lazy() here would add an unnecessary network waterfall.
// -----------------------------------------------------------------------------

export function BannerShell({
  colorScheme = "default",
  height = 580,
  bottomCurve,
  decoration,
  backgroundImage,
  children,
}: Pick<
  BannerProps,
  "colorScheme" | "height" | "bottomCurve" | "decoration" | "backgroundImage"
> & {
  children: ReactNode;
}) {
  const scheme = COLOR_SCHEMES[colorScheme];
  const curveStyle = bottomCurve?.style ?? "none";
  const curveScheme =
    bottomCurve?.color && bottomCurve.color in COLOR_SCHEMES
      ? COLOR_SCHEMES[bottomCurve.color]
      : null;
  const decorationStyle = decoration?.style ?? "none";
  const curveOvershootPx = 20;
  const curveEnabled = curveStyle === "curve-down" && curveScheme !== null;

  return (
    <>
      <div
        className={cn(
          "relative overflow-hidden flex flex-col px-6 isolate",
          BANNER_HEIGHT_REM[height],
          scheme.bg,
        )}
        style={
          backgroundImage
            ? {
                backgroundImage: `url(${backgroundImage.src})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : undefined
        }
      >
        {/* Circle decoration — behind the image */}
        {decorationStyle === "circle" && (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={`${process.env.NEXT_PUBLIC_BASE_PATH || ""}/decorations/circle-decoration-desktop.svg`}
              alt=""
              aria-hidden="true"
              className="absolute bottom-0 right-0 hidden lg:block h-auto pointer-events-none"
              style={{
                zIndex: Z.decoration,
                maxWidth:
                  "min(calc(5 / 12 * 128rem), calc(5 / 12 * (100% - 3.2rem)))",
              }}
            />
            <svg
              aria-hidden="true"
              className="absolute bottom-0 left-0 lg:hidden w-full pointer-events-none"
              viewBox="0 0 400 154"
              preserveAspectRatio="none"
              style={{
                zIndex: Z.decoration,
                height: "clamp(154px, 25vw, 250px)",
              }}
            >
              <circle cx="200" cy="751" r="750" fill="#D0F1F3" />
            </svg>
          </>
        )}

        {/* Variant content slot */}
        {children}

        {/* Bottom curve overlay */}
        {curveEnabled && curveScheme && (
          <div
            aria-hidden
            className={cn(
              "absolute left-0 right-0 pointer-events-none",
              curveScheme.bg,
            )}
            style={{
              zIndex: Z.curve,
              ["--curve-depth" as string]: "clamp(28px, 7vw, 75px)",
              top: `calc(100% - var(--curve-depth) - 1px)`,
              bottom: `-${curveOvershootPx}px`,
              clipPath: `shape(from 0 var(--curve-depth), curve to 100% var(--curve-depth) with 50% calc(-1 * (var(--curve-depth) - 1px)), vline to 100%, hline to 0, close)`,
            }}
          />
        )}
      </div>

      {/* Seam cover */}
      {curveEnabled && curveScheme && (
        <div aria-hidden className={cn("h-[2px] -mt-[2px]", curveScheme.bg)} />
      )}
    </>
  );
}
